/**
 * email-rail/helpers — pure, render-free building blocks for the
 * KanbanEmailAllocationRail: persistence keys + readers, the mobile
 * mutex event contract, the tri-state filter type, server-timing probe,
 * and the row formatters / drag-image builder. Split out of the former
 * monolith so the orchestrator + hook + sections stay under the
 * 500-line ceiling. Zero React — safe to import anywhere.
 */
import { readServerTimingMs, type ResourceTimingLike } from '../../utils/emailRailTiming'

/** Read the server-side ms for the most recent `/api/emails/` resource
 *  timing entry. Wrapped so a missing Performance API (jsdom / old
 *  browser) degrades to null instead of throwing. */
export function readEmailServerTimingMs(): number | null {
  try {
    if (
      typeof performance === 'undefined' ||
      typeof performance.getEntriesByType !== 'function'
    ) {
      return null
    }
    const entries = performance.getEntriesByType(
      'resource',
    ) as unknown as ResourceTimingLike[]
    return readServerTimingMs(entries, '/api/emails/')
  } catch {
    return null
  }
}

/** MIME used for emails dragged out of the rail. Picked so it
 *  doesn't collide with `text/x-kanban-card-id` (used by cards
 *  being moved between lanes) or `text/x-kanban-lane-id` (used
 *  by lane reorder gestures). Cards listen for this MIME and
 *  fire `onEmailAllocate(card, emailId)` on drop. */
export const KANBAN_ALLOCATE_EMAIL_MIME = 'text/x-kanban-allocate-email-id'

/** Persistence keys for the pinned + open flags. Kept in this
 *  module so the toolbar button + the rail itself read the same
 *  source of truth without an extra prop. */
export const PINNED_STORAGE_KEY = 'kanban:email-rail-pinned'
export const OPEN_STORAGE_KEY = 'kanban:email-rail-open'

/** MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §3.1 — mobile mutex bus.
 *  When the email rail opens on a phone (<md) it dispatches a
 *  CustomEvent on the window so the tenant's boards rail can
 *  close itself. Conversely the boards rail dispatches the
 *  reverse event so the email rail closes itself when the
 *  boards rail opens. Using a window event (vs a Zustand
 *  store or prop drilling through @quotech/ui) keeps the seam
 *  zero-API-surface — the universal kanban package doesn't
 *  need to know what's in the tenant shell. */
export const MOBILE_PANE_EVENT_NAME = 'kanban:mobile-pane-opened'
export type MobilePaneEventDetail = { pane: 'boards-list' | 'email-rail' }
/** Persistence for the All / Unallocated segmented control. The
 *  rail used to start every session in "Unallocated only" mode,
 *  which silently hid every allocated email — operators who'd
 *  already allocated their inbox saw an empty rail with the
 *  cryptic copy "Inbox zero" and assumed the email hub had
 *  broken. Persisting the choice means a power-user who toggles
 *  to "All" once stays there across reloads. First-visit default
 *  is still "Unallocated" because that IS the rail's primary use
 *  case (allocate fresh inbox to cards) — but the affordance is
 *  now an obvious 2-state segmented control instead of a single
 *  blue chip that read as a status badge. */
export const UNALLOCATED_ONLY_STORAGE_KEY = 'kanban:email-rail-unallocated-only'

/** Tri-state email filter for the rail's segmented control.
 *  Supersedes the old 2-state `unallocatedOnly` boolean:
 *    - 'unallocated' → server-filtered (no job AND not on a card)
 *    - 'all'         → server returns allocated + unallocated
 *    - 'allocated'   → fetch 'all', then client-filter to rows whose
 *                      `allocated` flag is true. Operator ask
 *                      2026-06-02 ("I want to see allocated emails
 *                      too"): pre-fix the only way to view an email
 *                      you'd already filed was the catch-all "All"
 *                      view mixed in with everything else; a dedicated
 *                      "Allocated" view answers "what have I already
 *                      put on the board?" in one click. Client-side
 *                      filtering keeps it zero-backend-risk (no new
 *                      query param / contract change). */
export type EmailRailFilterMode = 'all' | 'unallocated' | 'allocated'
export const FILTER_MODE_STORAGE_KEY = 'kanban:email-rail-filter-mode'

/** Header "New emails" → rail filter handshake. The rail owns filter
 *  state; the board header cannot prop-drill into a keep-alive panel, so
 *  it writes storage + dispatches this event. useEmailAllocationRail
 *  applies the mode when mounted (and on the event if already open). */
export const EMAIL_RAIL_FILTER_EVENT_NAME = 'kanban:email-rail-filter-mode'
export type EmailRailFilterEventDetail = { mode: EmailRailFilterMode }

/** I→O: persist + broadcast a rail filter mode for New-emails / host opens. */
export function requestEmailRailFilterMode(mode: EmailRailFilterMode): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(FILTER_MODE_STORAGE_KEY, mode)
    window.localStorage.setItem(
      UNALLOCATED_ONLY_STORAGE_KEY,
      mode === 'unallocated' ? '1' : '0',
    )
  } catch {
    /* ignore quota / private mode — still dispatch so in-memory listeners update */
  }
  window.dispatchEvent(
    new CustomEvent<EmailRailFilterEventDetail>(EMAIL_RAIL_FILTER_EVENT_NAME, {
      detail: { mode },
    }),
  )
}

export function readEmailRailFilterMode(): EmailRailFilterMode {
  if (typeof window === 'undefined') return 'unallocated'
  try {
    const v = window.localStorage.getItem(FILTER_MODE_STORAGE_KEY)
    if (v === 'all' || v === 'unallocated' || v === 'allocated') return v
    // Migration: fall back to the legacy boolean key so existing
    // operators land in the state they're used to (Unallocated by
    // default, or All if they'd toggled it off).
    return readEmailRailUnallocatedOnly() ? 'unallocated' : 'all'
  } catch {
    return 'unallocated'
  }
}

/** Read the pinned flag at module scope so the boardview can
 *  initialise its `open` state to match (pinned rails open by
 *  default; unpinned rails open only when the operator toggles
 *  the toolbar button). Wrapped in try/catch for SSR safety. */
export function readEmailRailPinned(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(PINNED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function readEmailRailOpen(): boolean {
  if (typeof window === 'undefined') return false
  try {
    // Below the `lg` breakpoint the rail is an overlay (see the
    // <aside> className) and the operator's expectation is that
    // a fresh page-load doesn't immediately cover the lanes with
    // it — they'd never be able to find the canvas. Operator
    // complaint 2026-05-08: "mobile view is fucked.. look at the
    // overlap disaster". Force-default to closed when narrow,
    // even if the desktop session left a `pinned=1` flag in
    // local storage. The rail stays one tap away via the
    // toolbar toggle.
    const isNarrow = typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 1023.98px)').matches
    if (isNarrow) return false
    // When pinned, default to open (operator asked for a permanent rail).
    // When unpinned, ALWAYS start closed on a fresh page load so the board
    // owns the full width. Restoring a stale `open=1` reserved a blank /
    // empty right band when the panel had nothing to paint (operator:
    // "large blank space on the right immediately after the board loads").
    // Deliberate opens still work: New emails / footer Emails / FAB /
    // dock launcher call setEmailRailOpen(true) and persist via the
    // open-effect in useEmailAllocationRail.
    if (readEmailRailPinned()) return true
    return false
  } catch {
    return false
  }
}

export function readEmailRailUnallocatedOnly(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const v = window.localStorage.getItem(UNALLOCATED_ONLY_STORAGE_KEY)
    if (v === '0') return false
    if (v === '1') return true
    // First visit (no key written yet) → keep the historical
    // default. Existing operators who already had this implicit
    // default land in the same state they're used to.
    return true
  } catch {
    return true
  }
}

export function formatRailDate(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
  } catch {
    return iso
  }
}

export function getInitials(label: string): string {
  if (!label) return '??'
  const local = label.includes('@') ? label.split('@')[0] : label
  const parts = local.split(/[.\s_-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return local.slice(0, 2).toUpperCase()
}

/**
 * Build a compact, branded drag-preview chip for an email row.
 *
 * WHY: native HTML5 DnD ghosts the ENTIRE source row (grip + avatar +
 * 2-line subject) — a bulky, washed-out snapshot that drifts under the
 * cursor and reads as "I picked up some UI" rather than "I'm allocating
 * this email". A small emerald chip ("✉ <label> · Allocate") makes the
 * gesture feel deliberate and matches the emerald drop-ring the target
 * card already paints (KanbanCard cardRing), so source + target speak
 * the same colour language.
 *
 * The element is appended off-screen; the browser snapshots it
 * synchronously during `dragstart`, after which the caller removes it
 * on `dragend`. All text goes through `textContent` (never innerHTML)
 * so a subject like `<script>` can't inject markup.
 */
export function buildEmailDragImage(sender: string, subject: string): HTMLElement {
  const el = document.createElement('div')
  el.setAttribute('data-kanban-email-drag-image', '')
  el.style.cssText = [
    'position:fixed',
    'top:-1000px',
    'left:-1000px',
    'pointer-events:none',
    'display:flex',
    'align-items:center',
    'gap:8px',
    'max-width:280px',
    'padding:7px 10px',
    'border-radius:10px',
    'background:#059669',
    'color:#ffffff',
    'font:600 12px/1.15 ui-sans-serif,system-ui,-apple-system,sans-serif',
    'box-shadow:0 12px 28px -8px rgba(5,150,105,0.6)',
    'white-space:nowrap',
  ].join(';')

  const icon = document.createElement('span')
  icon.textContent = '✉'
  icon.style.cssText = 'font-size:13px;line-height:1'

  const label = document.createElement('span')
  const raw = (sender || subject || 'Email').trim()
  label.textContent = raw.length > 40 ? `${raw.slice(0, 39)}…` : raw
  label.style.cssText = 'overflow:hidden;text-overflow:ellipsis;max-width:170px'

  const pill = document.createElement('span')
  pill.textContent = 'Allocate'
  pill.style.cssText = [
    'font-size:10px',
    'font-weight:700',
    'text-transform:uppercase',
    'letter-spacing:0.04em',
    'padding:1px 6px',
    'border-radius:999px',
    'background:rgba(255,255,255,0.22)',
  ].join(';')

  el.append(icon, label, pill)
  return el
}

/** Max board chips shown inline before collapsing the rest into a
 *  "+N" overflow pill — keeps a heavily-filed email's row height sane. */
export const MAX_BOARD_CHIPS = 3
